import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import CommonFooter from "../../CommonFooter/CommonFooter";
import classes from "./IntroductionB.module.css";
import { env } from "../../../constants/env";
import axios from "axios";
import CommonButton from "../../ui/CommonButton/CommonButton";
import InputField from "../../ui/InputField/InputField";

const IntroductionB = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const userRole = user?.role;
  const [editable, setEditable] = useState(userRole === "ASSESSOR");

  // ✅ Only metadata fields here
  const [formFields, setFormFields] = useState({
    assessor: "",
    observers: "",
    areaOfOperation: "",
    labRepresentative: "",
  });

  const handleFormFieldChange = (field, value) => {
    setFormFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (!labId) return;
    fetchAccreditation();
  }, [labId, token, user._id]);

  const fetchAccreditation = async () => {
    setIsFetching(true);
    axios
      .get(`${env.kataApiUrl}/api/accreditation`, {
        params: {
          labId,
          section: "Introduction",
          name: "SADCAS F134-B",
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.success && res.data.accreditation) {
          const accreditation = res.data.accreditation;

          if (accreditation.auditorId !== user._id || userRole !== "ASSESSOR") {
            setEditable(false);
          }

          // ✅ Prefill metadata fields if saved
          if (accreditation.metadata) {
            setFormFields({
              assessor: accreditation.metadata.assessor || "",
              observers: accreditation.metadata.observers || "",
              areaOfOperation: accreditation.metadata.areaOfOperation || "",
              labRepresentative: accreditation.metadata.labRepresentative || "",
            });
            setEditable(false);
          } else {
            setEditable(true);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to fetch accreditation:", err);
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const handleDone = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${env.kataApiUrl}/api/accreditation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: "SADCAS F134-B",
          labId,
          section: "Introduction",
          auditorId: user._id,
          answers: [], // ✅ no questions in B
          metadata: formFields,
        }),
      });

      if (!response.ok) throw new Error("Submission failed");

      fetchAccreditation();
      toast.success("Updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to submit assessment");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Introduction</h2>
        </div>
        {userRole === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditable(true)}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS B - Introduction has been submitted.
        </div>
      ) : (
        <>
          {/* ✅ Only metadata fields */}
          <div className="row mb-5">
            <div className="col-6 mb-3">
              <InputField
                labelText="Assessor/s"
                placeholder="Enter username"
                value={formFields.assessor}
                onChange={(e) =>
                  handleFormFieldChange("assessor", e.target.value)
                }
                disabled={!editable}
              />
            </div>
            <div className="col-6 mb-3">
              <InputField
                labelText="Observers"
                placeholder="Enter Observers"
                value={formFields.observers}
                onChange={(e) =>
                  handleFormFieldChange("observers", e.target.value)
                }
                disabled={!editable}
              />
            </div>
            <div className="col-6 mb-3">
              <InputField
                labelText="Area/Field of operation"
                placeholder="Enter area"
                value={formFields.areaOfOperation}
                onChange={(e) =>
                  handleFormFieldChange("areaOfOperation", e.target.value)
                }
                disabled={!editable}
              />
            </div>
            <div className="col-6 mb-3">
              <InputField
                labelText="Laboratory representative"
                placeholder="Enter representative"
                value={formFields.labRepresentative}
                onChange={(e) =>
                  handleFormFieldChange("labRepresentative", e.target.value)
                }
                disabled={!editable}
              />
            </div>
          </div>
        </>
      )}

      {editable && (
        <CommonFooter
          handlePrevious={() => navigate(-1)}
          handleNext={() => {}}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={0}
          questionSets={[{ title: "Introduction", content: null }]}
          doneText={isSubmitting ? "Submitting..." : "Update"}
          disabled={isSubmitting}
        />
      )}
    </div>
  );
};

export default IntroductionB;
